'use client'

import {useImperativeHandle, useRef} from "react";
import {FieldPath, FormProvider, useForm} from "react-hook-form"
import z from "zod/v4";
import {ErrorFormField} from "@/components/form/ErrorFormField";
import {SubmitButton} from "@/components/form/SubmitButton";
import {FORM_ROOT} from "@/components/form/const";
import {zodResolver} from "@hookform/resolvers/zod";
import {FormProps, OnSubmit, isErrorResult, isSuccessResult, ZodObjectType} from "./utils";

export function Form<S extends  ZodObjectType, O extends OnSubmit<S>>({
                                                                           children,
                                                                           submitButtonProps,
                                                                           onSubmit,
                                                                           schema,
                                                                           defaultValues,
                                                                           submitText = 'Submit',
                                                                           ref,
                                                                           ...props
                                                                         }: FormProps<S, O>) {
  const formEl = useRef<HTMLFormElement>(null)
  const form = useForm<z.infer<S>, z.infer<S>>({
    resolver: schema ? zodResolver<z.infer<S>, any, z.infer<S>>(schema as z.ZodType<any, any>) : undefined,
    defaultValues,
  })
  const _handleSubmit = (onSubmit: O) => form.handleSubmit(async (values) => {
    try {
      const result = (await onSubmit(values))

      //if a object is returned, mea  ns there is an error
      if (isErrorResult(result)) {
        form.setError(result.error?.path ? result.error?.path as FieldPath<z.infer<S>> : FORM_ROOT, {
          type: "submit",
          message: result.error?.message ?? "An unexpected error has occurred."
        })
        return result.error
      } else {
        form.reset()
        if (isSuccessResult(result)) {
          return result.success
        }
        return result
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      const message = "An unexpected error has occurred."
      form.setError(FORM_ROOT, {
        type: "submit",
        message: message
      })
    }

  })

  useImperativeHandle(ref, () => ({
    form: {
      ...form,
      handleSubmit: _handleSubmit,
    },
    formEl: formEl.current,
  }))

  return (
    <FormProvider {...form}>
      <form
        onChange={() => {
          form.setError(FORM_ROOT, {})
        }}
        ref={formEl}
        onSubmit={_handleSubmit(onSubmit)}
        {...props}
      >
        {/* Form fields supplied as children are rendered here */}
        {children}
        {(submitButtonProps || submitText) && (<>
            <ErrorFormField className={"w-full"}/>
            <SubmitButton
              {...submitButtonProps} >
              {submitText}
            </SubmitButton>
          </>
        )}
      </form>
    </FormProvider>
  )
}

Form.displayName = "Form";
